// Components
// Libs
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

// API
import getCart from '@/common/api/carts/getCart';
import postCartItems from '@/common/api/carts/items/postCartItems';
import Snack from '@/common/components/molecules/Snack/Snack';
// Constants
import { ENDPOINT_CARTS } from '@/common/constants';
// Contexts
import { useDefaultCartContext } from '@/common/contexts/DefaultCartContext';

const useCartAdd = ({ disabled, isLoading, isSuccess }) => {
  const { t } = useTranslation();
  const { defaultCart } = useDefaultCartContext();

  const {
    mutate: mutateGetCart,
    isLoading: isLoadingGetCart,
    isSuccess: isSuccessGetCart,
  } = useMutation(getCart);
  /**
   * Callback given to the children component to get cart
   * We need to call getCart to update the standalone cart
   * @param {object} cart
   * @param {function} onSuccess
   */
  const onUpdateCart = useCallback(
    ({ cart, onSuccess = () => {} }) => {
      mutateGetCart(
        {
          queryKey: [ENDPOINT_CARTS, cart.id, true],
        },
        {
          onSuccess,
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutateGetCart, t],
  );

  const {
    mutate: mutatePostCartItems,
    isLoading: isLoadingPostCartItems,
    isSuccess: isSuccessPostCartItems,
  } = useMutation(postCartItems);
  /**
   * Callback given to children component to add items to cart
   * @param {object} cart
   * @param {array} items
   * @param {function} onSuccess
   */
  const onAddToCart = useCallback(
    ({ items, cart = defaultCart, type, onSuccess = () => {} }) => {
      mutatePostCartItems(
        { items, cart, type },
        {
          onSuccess,
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [defaultCart, mutatePostCartItems, t],
  );

  /**
   * Check if action is disabled
   * @type {boolean}
   */
  const isDisabled = useMemo(
    () => ({
      loading: isLoading || isLoadingPostCartItems || isLoadingGetCart,
      disabled: isLoading || isLoadingPostCartItems || disabled,
    }),
    [disabled, isLoading, isLoadingGetCart, isLoadingPostCartItems],
  );

  return {
    isDisabled,
    isSuccess: isSuccess || isSuccessGetCart || isSuccessPostCartItems,
    onAddToCart,
    onUpdateCart,
  };
};

export default useCartAdd;
